(ns io.daskul.webapp.widgets.form.weekday-picker
  "Mobile-first weekday recurrence picker (#484).

   Seven compact toggles (M T W T F S S) instead of a dropdown multi-select —
   picking \"Mon/Wed/Fri\" is the single most common act when setting up a
   class, and on a phone a 7-tap-target row beats opening a list.

   All selection logic lives in the sibling `weekdays` namespace so it can be
   unit-tested under :node-test; this file is layout only."
  (:require
   [uix.core :as uix :refer [defui $]]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.widgets.form.weekdays :as wd]))

(defui weekday-toggle
  "One day button. Filled when selected, outlined when not.

   Three-letter labels rather than initials: M/T/W/T/F/S/S makes Tue/Thu and
   Sat/Sun indistinguishable except by position, which is a needless puzzle on
   a control people scan quickly. No tooltip — the label is already visible,
   and a tooltip repeating it is pure noise (and unreachable by touch)."
  [{:keys [day selected? disabled? on-toggle]}]
  ($ ui/button
     ;; No :color — "filled" picks up the theme's primary, so the selected
     ;; state matches whatever the product theme uses for primary actions.
     {:variant (if selected? "filled" "default")
      :size "compact-md"
      :radius "xl"
      :disabled disabled?
      :aria-pressed selected?
      :onClick #(on-toggle day)
      ;; Uniform width across all seven so the row reads as one control, and
      ;; wide enough for three letters while staying past the 44px touch
      ;; minimum. Seven of these wrap to a second line on a narrow phone,
      ;; which is the intended trade for unambiguous labels.
      :styles #js {:root #js {:minWidth "56px" :paddingLeft 4 :paddingRight 4}}}
     (wd/day-labels day)))

(defui weekday-picker-field
  "Weekday selection as a wrapping row of toggles.

   Props:
   - label      - field label
   - value      - vector of day tokens (\"mon\" … \"sun\")
   - on-change  - called with the new, canonically-ordered vector
   - required?  - show the required marker
   - disabled?  - disable every toggle
   - error      - field error message
   - description- helper text; falls back to a summary of the selection"
  [{:keys [label value on-change required? disabled? error description]}]
  (let [days (wd/normalize value)
        handle-toggle (fn [day] (on-change (wd/toggle days day)))]
    ($ ui/stack {:gap "xs"}
       (when label
         ($ ui/text {:size "sm" :fw 500}
            label
            (when required?
              ($ ui/text {:span true :c "red" :aria-hidden true} " *"))))

       ;; wrap: on a narrow phone the seven toggles reflow instead of
       ;; overflowing horizontally.
       ($ ui/group {:gap "xs" :wrap "wrap" :role "group" :aria-label (or label "Weekdays")}
          (for [day wd/ordered-days]
            ($ weekday-toggle
               {:key day
                :day day
                :selected? (wd/selected? days day)
                :disabled? disabled?
                :on-toggle handle-toggle})))

       ($ ui/text {:size "xs" :c (if error "red" "dimmed")}
          (or error description (wd/summary days))))))
